import { useMemo, useRef, useState, type ReactElement } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { ArrowLeft, Download, FileUp, Upload } from "lucide-react";
import { Badge, Button, Stack, Surface, Text } from "@ux/primitives";
import { DataTable, Toolbar, type DataColumn } from "@ux/compositions";
import { palette, space } from "@ux/tokens";
import { useCanEdit } from "@shell/auth/useCanEdit";
import { AvailableOfficesPanel } from "../../sage-offices/AvailableOfficesPanel";
import { useSageOffices } from "../../sage-offices/hooks";
import { describeApiError } from "../../common/errors";
import { triggerCsvDownload } from "../../common/csv";
import { useBulkImportWorkspaces } from "../hooks";
import { BULK_IMPORT_WORKSPACES_MAX_ROWS, type BulkImportWorkspacesResult } from "../types";
import {
    buildFailedRowsCsv,
    buildWorkspaceCsvTemplate,
    parseWorkspacesCsv,
    type ParsedWorkspaceRow,
    type ParseWorkspacesCsvResult,
} from "./parseWorkspacesCsv";

/**
 * Bulk-import flow for the initial campaign load. The page is a small
 * state machine — idle → preview → importing → result — with the
 * preview and result steps both rendering a `DataTable` so the
 * operator gets the same scannable layout for "what's about to be
 * imported" and "what happened."
 *
 * The page is intentionally a dedicated route (not a modal) because
 * a 500-row preview table needs the full screen, and because the
 * operator may want to bookmark/refresh during a long campaign
 * session without losing their place.
 */
type PageState =
    | { kind: "idle"; fileName: string | null }
    | { kind: "preview"; fileName: string; parse: ParseWorkspacesCsvResult }
    | { kind: "importing"; fileName: string; parse: ParseWorkspacesCsvResult }
    | {
          kind: "result";
          fileName: string;
          parse: ParseWorkspacesCsvResult;
          result: BulkImportWorkspacesResult;
      };

export function BulkImportWorkspacesPage(): ReactElement {
    const canEdit = useCanEdit();
    const navigate = useNavigate();
    const fileInputRef = useRef<HTMLInputElement>(null);
    const [state, setState] = useState<PageState>({ kind: "idle", fileName: null });
    const [readError, setReadError] = useState<string | null>(null);

    // Offices come from the same query the rest of the workspaces UI
    // uses. We need them to validate sageOfficeId at parse time so the
    // operator sees "office 'SO-XYZ' doesn't exist" *before* hitting
    // the server with 247 rows that all fail for the same reason.
    const officesQuery = useSageOffices({ q: "", includeDeleted: true });
    const offices = useMemo(() => officesQuery.data ?? [], [officesQuery.data]);

    const importMutation = useBulkImportWorkspaces();

    function pickFile(): void {
        // Browsers suppress the change event when the user re-picks
        // the same file path, so editing the CSV on disk and clicking
        // "Pick another file" would silently keep the prior parse.
        // Resetting the input value first guarantees the next
        // selection always fires onChange, even when the path is
        // unchanged.
        if (fileInputRef.current !== null) fileInputRef.current.value = "";
        fileInputRef.current?.click();
    }

    async function handleFileChosen(file: File): Promise<void> {
        setReadError(null);
        try {
            const text = await file.text();
            const parse = parseWorkspacesCsv(text, offices);
            // Enforce the per-request cap at parse time too, so the
            // operator gets a clear message before importing rather
            // than a 400 from the server.
            if (parse.rows.length > BULK_IMPORT_WORKSPACES_MAX_ROWS) {
                setReadError(
                    `This file has ${parse.rows.length} rows; the maximum per import is ${BULK_IMPORT_WORKSPACES_MAX_ROWS}. Split the file and import in batches.`,
                );
                setState({ kind: "idle", fileName: file.name });
                return;
            }
            setState({ kind: "preview", fileName: file.name, parse });
        } catch (e) {
            setReadError(`Could not read file: ${(e as Error).message}`);
            setState({ kind: "idle", fileName: file.name });
        }
    }

    function handleStartImport(): void {
        if (state.kind !== "preview") return;
        if (state.parse.hasErrors) return;
        const items = state.parse.rows.map((r) => r.payload);
        setState({ kind: "importing", fileName: state.fileName, parse: state.parse });
        importMutation.mutate(
            { items },
            {
                onSuccess: (result) => {
                    setState({
                        kind: "result",
                        fileName: state.fileName,
                        parse: state.parse,
                        result,
                    });
                },
                onError: () => {
                    // Stay in preview; mutation error surfaces via importMutation.error.
                    setState({ kind: "preview", fileName: state.fileName, parse: state.parse });
                },
            },
        );
    }

    function handleStartOver(): void {
        setReadError(null);
        setState({ kind: "idle", fileName: null });
        importMutation.reset();
        if (fileInputRef.current !== null) fileInputRef.current.value = "";
    }

    function handleDownloadTemplate(): void {
        // Pass the loaded offices so the template's example rows use a
        // real id from the user's tenant when available — making the
        // downloaded file importable as-is.
        triggerCsvDownload("workspaces-template.csv", buildWorkspaceCsvTemplate(offices));
    }

    function handleDownloadFailedRows(): void {
        if (state.kind !== "result") return;
        // Correlate per-row server outcomes back to the parsed rows
        // by id (the only field that survives intact through the
        // round trip — name/type are echoed but id is the primary key).
        const rowsById = new Map<string, ParsedWorkspaceRow>();
        for (const r of state.parse.rows) rowsById.set(r.payload.id, r);
        const failed = state.result.results
            .filter((r) => r.outcome === "failed")
            .flatMap((r) => {
                const row = rowsById.get(r.id);
                if (row === undefined) return [];
                return [{ row, errorMessage: r.errorMessage ?? "Unknown error." }];
            });
        if (failed.length === 0) return;
        triggerCsvDownload("workspaces-failed.csv", buildFailedRowsCsv(failed));
    }

    if (!canEdit) {
        // Bulk import is a write-only flow; viewers landing here via a
        // bookmarked URL get bounced back to the Workspaces list, where the
        // global view-only banner explains the missing role.
        return <Navigate to="/admin/workspaces" replace />;
    }

    return (
        <Stack direction="column" gap={0} grow style={{ overflow: "hidden" }}>
            <Toolbar
                leading={
                    <Stack direction="row" gap={2} align="center">
                        <Button
                            variant="ghost"
                            size="sm"
                            leadingIcon={<ArrowLeft size={14} />}
                            onClick={() => navigate("/admin/workspaces")}
                        >
                            Workspaces
                        </Button>
                        <Text role="heading">Bulk import</Text>
                    </Stack>
                }
                trailing={
                    <Button
                        variant="ghost"
                        size="sm"
                        leadingIcon={<Download size={14} />}
                        onClick={handleDownloadTemplate}
                    >
                        Download template
                    </Button>
                }
            />

            <div style={{ flex: 1, overflow: "auto", padding: space[5] }}>
                <Stack gap={4} style={{ maxWidth: 1100 }}>
                    {state.kind === "idle" ? (
                        <>
                            <IdleStep
                                fileName={state.fileName}
                                officesPending={officesQuery.isPending}
                                onPickFile={pickFile}
                                error={readError}
                            />
                            {/* Reference panel lives next to the picker
                                so the operator can copy the office id
                                they need *before* opening the spreadsheet,
                                not after seeing per-row errors. */}
                            <AvailableOfficesPanel offices={offices} />
                        </>
                    ) : null}

                    {state.kind === "preview" ? (
                        <>
                            <PreviewStep
                                fileName={state.fileName}
                                parse={state.parse}
                                isImporting={false}
                                importError={
                                    importMutation.isError
                                        ? describeApiError(importMutation.error)
                                        : null
                                }
                                onStartOver={handleStartOver}
                                onPickAnother={pickFile}
                                onImport={handleStartImport}
                            />
                            {/* Keep the reference panel visible while
                                fixing rows, so the operator doesn't
                                bounce between tabs to look up an id. */}
                            <AvailableOfficesPanel offices={offices} />
                        </>
                    ) : null}

                    {state.kind === "importing" ? (
                        <PreviewStep
                            fileName={state.fileName}
                            parse={state.parse}
                            isImporting={true}
                            importError={null}
                            onStartOver={handleStartOver}
                            onPickAnother={pickFile}
                            onImport={handleStartImport}
                        />
                    ) : null}

                    {state.kind === "result" ? (
                        <ResultStep
                            result={state.result}
                            parse={state.parse}
                            onStartOver={handleStartOver}
                            onDownloadFailed={handleDownloadFailedRows}
                            onBackToWorkspaces={() => navigate("/admin/workspaces")}
                        />
                    ) : null}
                </Stack>
            </div>

            {/* Hidden input so we can trigger from any styled button without
                fighting native file-input chrome. */}
            <input
                ref={fileInputRef}
                type="file"
                accept=".csv,text/csv"
                style={{ display: "none" }}
                onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (file !== undefined) {
                        void handleFileChosen(file);
                    }
                }}
            />
        </Stack>
    );
}

interface IdleStepProps {
    readonly fileName: string | null;
    readonly officesPending: boolean;
    readonly onPickFile: () => void;
    readonly error: string | null;
}

function IdleStep({ fileName, officesPending, onPickFile, error }: IdleStepProps): ReactElement {
    return (
        <Surface tone="raised" padding={5}>
            <Stack gap={4}>
                <Stack gap={1}>
                    <Text role="heading">Upload a workspaces CSV</Text>
                    <Text role="muted">
                        Header row required. Columns:{" "}
                        <code>sageOfficeId,type,name,friendlyName,floorplanElementId</code>. Up to{" "}
                        {BULK_IMPORT_WORKSPACES_MAX_ROWS.toLocaleString()} rows per import.
                    </Text>
                </Stack>

                <Stack direction="row" gap={2} align="center" wrap>
                    <Button
                        variant="primary"
                        leadingIcon={<FileUp size={16} />}
                        onClick={onPickFile}
                        disabled={officesPending}
                    >
                        {officesPending ? "Loading offices…" : "Choose CSV file"}
                    </Button>
                    {fileName !== null ? <Text role="muted">Last picked: {fileName}</Text> : null}
                </Stack>

                {error !== null ? (
                    <Surface tone="inset" padding={3}>
                        <Text role="muted" color={palette.danger}>
                            {error}
                        </Text>
                    </Surface>
                ) : null}

                <Stack gap={1}>
                    <Text role="eyebrow">How this works</Text>
                    <Text role="muted">
                        Each row is created independently. Re-running the same file is safe — rows
                        already imported come back as <em>skipped</em> rather than duplicated, so
                        you can fix failed rows and re-upload until the run is clean.
                    </Text>
                </Stack>
            </Stack>
        </Surface>
    );
}

interface PreviewStepProps {
    readonly fileName: string;
    readonly parse: ParseWorkspacesCsvResult;
    readonly isImporting: boolean;
    readonly importError: string | null;
    readonly onStartOver: () => void;
    readonly onPickAnother: () => void;
    readonly onImport: () => void;
}

function PreviewStep({
    fileName,
    parse,
    isImporting,
    importError,
    onStartOver,
    onPickAnother,
    onImport,
}: PreviewStepProps): ReactElement {
    if (parse.fatalError !== null) {
        return (
            <Surface tone="raised" padding={5}>
                <Stack gap={3}>
                    <Stack gap={1}>
                        <Text role="heading">Couldn't parse {fileName}</Text>
                        <Text role="muted" color={palette.danger}>
                            {parse.fatalError}
                        </Text>
                    </Stack>
                    <Stack direction="row" gap={2}>
                        <Button variant="primary" onClick={onPickAnother}>
                            Pick a different file
                        </Button>
                        <Button variant="ghost" onClick={onStartOver}>
                            Start over
                        </Button>
                    </Stack>
                </Stack>
            </Surface>
        );
    }

    const errorRowCount = parse.rows.filter((r) => r.errors.length > 0).length;
    const okRowCount = parse.rows.length - errorRowCount;

    const columns: readonly DataColumn<ParsedWorkspaceRow>[] = [
        {
            id: "row",
            header: "Row",
            width: "60px",
            render: (r) => <Text role="muted">{r.rowIndex}</Text>,
        },
        {
            id: "name",
            header: "Workspace",
            render: (r) => (
                <Stack gap={0}>
                    <Text role="body" weight="semibold">
                        {r.payload.name || <em>(missing)</em>}
                    </Text>
                    {r.payload.friendlyName !== null ? (
                        <Text role="muted">{r.payload.friendlyName}</Text>
                    ) : null}
                </Stack>
            ),
        },
        {
            id: "type",
            header: "Type",
            width: "100px",
            render: (r) => <Text role="body">{r.payload.type || "—"}</Text>,
        },
        {
            id: "office",
            header: "Sage office",
            render: (r) =>
                r.sageOfficeName !== null ? (
                    <Stack gap={0}>
                        <Text role="body">{r.sageOfficeName}</Text>
                        <Text role="muted">{r.payload.sageOfficeId}</Text>
                    </Stack>
                ) : (
                    <Text role="muted">{r.payload.sageOfficeId || "—"}</Text>
                ),
        },
        {
            id: "status",
            header: "Status",
            render: (r) =>
                r.errors.length === 0 ? (
                    <Badge tone="ok">Ready</Badge>
                ) : (
                    <Stack gap={1}>
                        <Badge tone="danger">
                            {r.errors.length} issue{r.errors.length === 1 ? "" : "s"}
                        </Badge>
                        {r.errors.map((msg, i) => (
                            <Text key={i} role="muted" color={palette.danger}>
                                {msg}
                            </Text>
                        ))}
                    </Stack>
                ),
        },
    ];

    return (
        <Stack gap={3}>
            <Surface tone="raised" padding={4}>
                <Stack direction="row" align="center" justify="space-between" wrap gap={3}>
                    <Stack gap={1}>
                        <Text role="heading">Preview · {fileName}</Text>
                        <Text role="muted">
                            {parse.rows.length} row{parse.rows.length === 1 ? "" : "s"} parsed ·{" "}
                            <strong>{okRowCount}</strong> ready
                            {errorRowCount > 0 ? (
                                <>
                                    {" · "}
                                    <span style={{ color: palette.danger }}>
                                        <strong>{errorRowCount}</strong> with issues
                                    </span>
                                </>
                            ) : null}
                        </Text>
                    </Stack>
                    <Stack direction="row" gap={2} wrap>
                        <Button variant="ghost" onClick={onPickAnother} disabled={isImporting}>
                            Pick another file
                        </Button>
                        <Button
                            variant="primary"
                            leadingIcon={<Upload size={16} />}
                            onClick={onImport}
                            disabled={isImporting || parse.hasErrors || parse.rows.length === 0}
                        >
                            {isImporting
                                ? `Importing ${parse.rows.length} row${parse.rows.length === 1 ? "" : "s"}…`
                                : `Import ${okRowCount} row${okRowCount === 1 ? "" : "s"}`}
                        </Button>
                    </Stack>
                </Stack>
            </Surface>

            {importError !== null ? (
                <Surface tone="raised" padding={3}>
                    <Text role="muted" color={palette.danger}>
                        Import failed: {importError}
                    </Text>
                </Surface>
            ) : null}

            {parse.hasErrors ? (
                <Surface tone="inset" padding={3}>
                    <Text role="muted" color={palette.danger}>
                        Fix the highlighted rows in your CSV and re-upload. Import is disabled until
                        every row is ready.
                    </Text>
                </Surface>
            ) : null}

            <Surface tone="raised" padding={0}>
                <DataTable<ParsedWorkspaceRow>
                    columns={columns}
                    rows={parse.rows}
                    rowKey={(r) => r.payload.id}
                />
            </Surface>
        </Stack>
    );
}

interface ResultStepProps {
    readonly result: BulkImportWorkspacesResult;
    readonly parse: ParseWorkspacesCsvResult;
    readonly onStartOver: () => void;
    readonly onDownloadFailed: () => void;
    readonly onBackToWorkspaces: () => void;
}

function ResultStep({
    result,
    parse,
    onStartOver,
    onDownloadFailed,
    onBackToWorkspaces,
}: ResultStepProps): ReactElement {
    // Cross-reference parsed rows with server outcomes for the
    // failures table. We use the row id as the join key — it survives
    // intact across the round trip and is the only field guaranteed
    // unique within the request.
    const failedRows = useMemo(() => {
        const rowsById = new Map<string, ParsedWorkspaceRow>();
        for (const r of parse.rows) rowsById.set(r.payload.id, r);
        return result.results
            .filter((r) => r.outcome === "failed")
            .flatMap((r) => {
                const row = rowsById.get(r.id);
                if (row === undefined) return [];
                return [{ row, errorMessage: r.errorMessage ?? "Unknown error." }];
            });
    }, [parse.rows, result.results]);

    const allClean = result.failed === 0;

    const failedColumns: readonly DataColumn<{
        readonly row: ParsedWorkspaceRow;
        readonly errorMessage: string;
    }>[] = [
        {
            id: "row",
            header: "Row",
            width: "60px",
            render: (r) => <Text role="muted">{r.row.rowIndex}</Text>,
        },
        {
            id: "name",
            header: "Workspace",
            render: (r) => <Text role="body">{r.row.payload.name || "—"}</Text>,
        },
        {
            id: "office",
            header: "Sage office",
            render: (r) => <Text role="body">{r.row.payload.sageOfficeId || "—"}</Text>,
        },
        {
            id: "error",
            header: "Error",
            render: (r) => (
                <Text role="muted" color={palette.danger}>
                    {r.errorMessage}
                </Text>
            ),
        },
    ];

    return (
        <Stack gap={3}>
            <Surface tone="raised" padding={4}>
                <Stack gap={3}>
                    <Stack gap={1}>
                        <Text role="heading">
                            {allClean ? "Import complete" : "Import finished with issues"}
                        </Text>
                        <Text role="muted">
                            <strong>{result.created}</strong> created
                            {result.skipped > 0 ? (
                                <>
                                    {" · "}
                                    <strong>{result.skipped}</strong> skipped (already existed)
                                </>
                            ) : null}
                            {result.failed > 0 ? (
                                <>
                                    {" · "}
                                    <span style={{ color: palette.danger }}>
                                        <strong>{result.failed}</strong> failed
                                    </span>
                                </>
                            ) : null}
                            {" · "}
                            <strong>{result.totalRows}</strong> total
                        </Text>
                    </Stack>

                    <Stack direction="row" gap={2} wrap>
                        <Button variant="primary" onClick={onBackToWorkspaces}>
                            Back to workspaces
                        </Button>
                        <Button variant="ghost" onClick={onStartOver}>
                            Import another file
                        </Button>
                        {result.failed > 0 ? (
                            <Button
                                variant="secondary"
                                leadingIcon={<Download size={14} />}
                                onClick={onDownloadFailed}
                            >
                                Download failed rows
                            </Button>
                        ) : null}
                    </Stack>
                </Stack>
            </Surface>

            {failedRows.length > 0 ? (
                <Surface tone="raised" padding={0}>
                    <DataTable<{ readonly row: ParsedWorkspaceRow; readonly errorMessage: string }>
                        columns={failedColumns}
                        rows={failedRows}
                        rowKey={(r) => r.row.payload.id}
                    />
                </Surface>
            ) : null}
        </Stack>
    );
}
